import { createContext, useContext, useState } from "react";

export const ProductRuleContext = createContext();

const normalizeSelectOption = (option) => {
     if (option === null || option === undefined || option === "") return null;
     if (typeof option !== "object") {
          const value = String(option);
          return { label: value, value };
     }
     const value = option.value ?? option.channelName ?? option.lang ?? option.label;
     if (value === null || value === undefined || value === "") return null;
     return {
          ...option,
          label: String(option.label ?? option.channelName ?? value),
          value
     };
};

/** @typedef {Object} LanguageAndChannelHelper
 * @property {(active: boolean) => void} toggleLanguageAndChannel
 * @property {() => boolean} isLanguageAndChannelActive
 * @property {(language: string|null) => void} setLanguage
 * @property {() => string|null} getLanguage
 * @property {(channel: string|null) => void} setChannel
 * @property {() => string|null} getChannel
 * @property {() => boolean} isConfigured
 */

/** @typedef {Object} StockHelper
 * @property {() => Object[]} getStockRules
 * @property {(stockRule: Object) => string} addStockRule
 * @property {(ruleId: string) => void} removeStockRule
 * @property {(ruleId: string, field: string, value: any) => void} updateStockRule
 * @property {(ruleId: string) => Object|null} getStockRule
 * @property {() => boolean} hasActiveStockRules
 * @property {() => boolean} hasConfiguredStockRules
 */

/** @typedef {Object} ProductAttributeHelper
 * @property {(active: boolean) => void} toggleProductAttribute
 * @property {() => boolean} isProductAttributeActive
 * @property {(equation: string|null) => void} setEquation
 * @property {() => string|null} getEquation
 * @property {() => boolean} isConfigured
 * @property {() => void} clear
 */

/** @typedef {Object} RankingAndFilterHelper
 * @property {(active: boolean) => void} toggleRankingAndFilter
 * @property {() => boolean} isRankingAndFilterActive
 * @property {(number|null) => void} setRankingFirst
 * @property {() => number|null} getRankingFirst
 * @property {(number|null) => void} setRankingSecond
 * @property {() => number|null} getRankingSecond
 * @property {(number|null) => void} setPriceFirst
 * @property {() => number|null} getPriceFirst
 * @property {(number|null) => void} setPriceSecond
 * @property {() => number|null} getPriceSecond
 * @property {() => boolean} isConfigured
 * @property {() => void} clear
 */

/** @typedef {Object} ProductCategoryHelper
 * @property {(active: boolean) => void} toggleProductCategory
 * @property {() => boolean} isProductCategoryActive
 * @property {(department: string|null) => void} setDepartment
 * @property {() => string|null} getDepartment
 * @property {(area: string|null) => void} setArea
 * @property {() => string|null} getArea
 * @property {(category: string|null) => void} setCategory
 * @property {() => string|null} getCategory
 * @property {(article: string|null) => void} setArticle
 * @property {() => string|null} getArticle
 * @property {(provider: string|null) => void} setProvider
 * @property {() => string|null} getProvider
 * @property {(productNrList: string|null) => void} setProductNrList
 * @property {() => string|null} getProductNrList
 * @property {(filterProductNr: boolean) => void} setFilterProductNr
 * @property {() => boolean} getFilterProductNr
 * @property {() => boolean} isConfigured
 * @property {() => void} clear
 */

/** @typedef {Object} ProductRuleHelper
 * @property {() => Object[]} getActiveRules
 */

/** @typedef {Object} ProductRuleContextValue
 * @property {LanguageAndChannelHelper} LanguageAndChannelHelper
 * @property {StockHelper} StockHelper
 * @property {ProductAttributeHelper} ProductAttributeHelper
 * @property {RankingAndFilterHelper} RankingAndFilterHelper
 * @property {ProductCategoryHelper} ProductCategoryHelper
 * @property {ProductRuleHelper} ProductRuleHelper
 */

export const ProductRuleContextProvider = ({ children }) => {
     const [productRuleData, setProductRuleData] = useState({
          languageAndChannel: { keyString: "languageAndChannel", name: "Sprache & Kanal", active: true, language: { label: "de", value: "de" }, channel: { label: "Onlineshop", value: "Onlineshop", id: 1 } },
          stockRules: [],
          "product-attribute": { keyString: "product-attribute", name: "Produkt-Attribute", active: false, equation: [] },
          rankingAndFilter: { keyString: "rankingAndFilter", name: "Rankin- & Preis-Filter", active: false, rankingFirst: null, rankingSecond: null, priceFirst: null, priceSecond: null },
          productCategory: { keyString: "productCategory", name: "Produkt-Kategorie", active: false, department: null, area: null, category: null, article: null, provider: null, productNrList: null, filterProductNr: true },
     });

     const updateField = (section, field, value) => {
          setProductRuleData((prev) => ({
               ...prev,
               [section]: { ...prev[section], [field]: value },
          }));
     };

     const toggleSection = (section, active) => updateField(section, "active", active);

     const generateStockRuleId = () => `stock_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

     const addStockRule = (stockRule = {}) => {
          const ruleId = generateStockRuleId();
          const newStockRule = {
               id: ruleId,
               keyString: "stock",
               name: "Verfügbarkeit",
               active: true,
               stock: [],
               ...stockRule,
               border_style: stockRule.border_style || `border_${ruleId}`,
          };

          setProductRuleData(prev => ({
               ...prev,
               stockRules: [...prev.stockRules, newStockRule].sort((left, right) => {
                    const leftTime = parseInt(left.id.split("_")[1], 10);
                    const rightTime = parseInt(right.id.split("_")[1], 10);
                    return leftTime - rightTime;
               })
          }));

          return ruleId;
     };

     const removeStockRule = (ruleId) => {
          setProductRuleData(prev => ({
               ...prev,
               stockRules: prev.stockRules.filter(rule => rule.id !== ruleId)
          }));
     };

     const updateStockRule = (ruleId, field, value) => {
          setProductRuleData(prev => ({
               ...prev,
               stockRules: prev.stockRules.map(rule =>
                    rule.id === ruleId ? { ...rule, [field]: value } : rule
               )
          }));
     };

     const getStockRule = (ruleId) => {
          return productRuleData.stockRules.find(rule => rule.id === ruleId) || null;
     };

     const isStockRuleConfigured = (rule) => {
          if (!rule || !Array.isArray(rule.stock)) {
               return false;
          }
          return rule.stock.some(Boolean);
     };

     return (
          <ProductRuleContext.Provider
               /** @type {ProductRuleContextValue} */
               value={{
                    LanguageAndChannelHelper: {
                         toggleLanguageAndChannel: (active) => toggleSection("languageAndChannel", active),
                         isLanguageAndChannelActive: () => productRuleData.languageAndChannel.active,
                         setLanguage: (val) => updateField("languageAndChannel", "language", normalizeSelectOption(val)),
                         getLanguage: () => productRuleData.languageAndChannel.language,
                         setChannel: (val) => updateField("languageAndChannel", "channel", normalizeSelectOption(val)),
                         getChannel: () => productRuleData.languageAndChannel.channel,
                         isConfigured: () => {
                              const lang = productRuleData.languageAndChannel.language;
                              const channel = productRuleData.languageAndChannel.channel;
                              return !!lang && !!channel;
                         }
                    },

                    StockHelper: {
                         getStockRules: () => productRuleData.stockRules,
                         addStockRule,
                         removeStockRule,
                         updateStockRule,
                         getStockRule,
                         hasActiveStockRules: () => productRuleData.stockRules.length > 0,
                         hasConfiguredStockRules: () =>
                              productRuleData.stockRules.some((rule) => isStockRuleConfigured(rule)),
                    },

                    ProductAttributeHelper: {
                         toggleProductAttribute: (active) => toggleSection("product-attribute", active),
                         isProductAttributeActive: () => productRuleData["product-attribute"].active,
                         setEquation: (val) => updateField("product-attribute", "equation", val),
                         getEquation: () => productRuleData["product-attribute"].equation || [],
                         addToEquation: (term) => {
                              const currentEquation = productRuleData["product-attribute"].equation || [];
                              updateField("product-attribute", "equation", [...currentEquation, term]);
                         },
                         removeLastFromEquation: () => {
                              const currentEquation = productRuleData["product-attribute"].equation || [];
                              if (currentEquation.length > 0) {
                                   updateField("product-attribute", "equation", currentEquation.slice(0, -1));
                              }
                         },
                         clearEquation: () => updateField("product-attribute", "equation", []),
                         isConfigured: () => {
                              const eq = productRuleData["product-attribute"].equation;
                              return Array.isArray(eq) && eq.length > 0;
                         },
                         clear: () => {
                              toggleSection("product-attribute", false);
                              updateField("product-attribute", "equation", []);
                         }
                    },

                    RankingAndFilterHelper: {
                         toggleRankingAndFilter: (active) => toggleSection("rankingAndFilter", active),
                         isRankingAndFilterActive: () => productRuleData.rankingAndFilter.active,
                         setRankingFirst: (val) => updateField("rankingAndFilter", "rankingFirst", val),
                         getRankingFirst: () => productRuleData.rankingAndFilter.rankingFirst,
                         setRankingSecond: (val) => updateField("rankingAndFilter", "rankingSecond", val),
                         getRankingSecond: () => productRuleData.rankingAndFilter.rankingSecond,
                         setPriceFirst: (val) => updateField("rankingAndFilter", "priceFirst", val),
                         getPriceFirst: () => productRuleData.rankingAndFilter.priceFirst,
                         setPriceSecond: (val) => updateField("rankingAndFilter", "priceSecond", val),
                         getPriceSecond: () => productRuleData.rankingAndFilter.priceSecond,
                         isConfigured: () => {
                              const data = productRuleData.rankingAndFilter;
                              const hasValue = (val) =>
                                   val !== null &&
                                   val !== undefined &&
                                   !(typeof val === "string" && val.trim() === "");
                              return (
                                   hasValue(data.rankingFirst) ||
                                   hasValue(data.rankingSecond) ||
                                   hasValue(data.priceFirst) ||
                                   hasValue(data.priceSecond)
                              );
                         },
                         clear: () => {
                              toggleSection("rankingAndFilter", false);
                              updateField("rankingAndFilter", "rankingFirst", null);
                              updateField("rankingAndFilter", "rankingSecond", null);
                              updateField("rankingAndFilter", "priceFirst", null);
                              updateField("rankingAndFilter", "priceSecond", null);
                         }
                    },

                    ProductCategoryHelper: {
                         toggleProductCategory: (active) => toggleSection("productCategory", active),
                         isProductCategoryActive: () => productRuleData.productCategory.active,
                         setDepartment: (val) => updateField("productCategory", "department", val),
                         getDepartment: () => productRuleData.productCategory.department,
                         setArea: (val) => updateField("productCategory", "area", val),
                         getArea: () => productRuleData.productCategory.area,
                         setCategory: (val) => updateField("productCategory", "category", val),
                         getCategory: () => productRuleData.productCategory.category,
                         setArticle: (val) => updateField("productCategory", "article", val),
                         getArticle: () => productRuleData.productCategory.article,
                         setProvider: (val) => updateField("productCategory", "provider", val),
                         getProvider: () => productRuleData.productCategory.provider,
                         setProductNrList: (val) => updateField("productCategory", "productNrList", val),
                         getProductNrList: () => productRuleData.productCategory.productNrList,
                         setFilterProductNr: (val) => updateField("productCategory", "filterProductNr", val),
                         getFilterProductNr: () => productRuleData.productCategory.filterProductNr,
                         isConfigured: () => {
                              const data = productRuleData.productCategory;

                              const hasDept = Array.isArray(data.department) ? data.department.length > 0 : !!data.department;
                              const hasArea = Array.isArray(data.area) ? data.area.length > 0 : !!data.area;
                              const hasCategory = Array.isArray(data.category) ? data.category.length > 0 : !!data.category;
                              const hasArticle = Array.isArray(data.article) ? data.article.length > 0 : !!data.article;
                              const hasProvider = Array.isArray(data.provider) ? data.provider.length > 0 : !!data.provider;
                              const hasProductNrList =
                                   typeof data.productNrList === "string" && data.productNrList.trim().length > 0;

                              return hasDept || hasArea || hasCategory || hasArticle || hasProvider || hasProductNrList;
                         },
                         clear: () => {
                              toggleSection("productCategory", false);
                              updateField("productCategory", "department", null);
                              updateField("productCategory", "area", null);
                              updateField("productCategory", "category", null);
                              updateField("productCategory", "article", null);
                              updateField("productCategory", "provider", null);
                              updateField("productCategory", "productNrList", null);
                              updateField("productCategory", "filterProductNr", true);
                         }
                    },

                    ProductRuleHelper: {
                         getActiveRules: () => [
                              ...Object.values(productRuleData).filter((data) =>
                                   typeof data === "object" &&
                                   data.active &&
                                   data.keyString !== undefined &&
                                   !Array.isArray(data) &&
                                   (data.keyString === "languageAndChannel"
                                        ? true
                                        : data.keyString === "product-attribute"
                                             ? Array.isArray(data.equation) && data.equation.length > 0
                                             : data.keyString === "rankingAndFilter"
                                                  ? (() => {
                                                       const hasValue = (val) =>
                                                            val !== null &&
                                                            val !== undefined &&
                                                            !(typeof val === "string" && val.trim() === "");
                                                       return (
                                                            hasValue(data.rankingFirst) ||
                                                            hasValue(data.rankingSecond) ||
                                                            hasValue(data.priceFirst) ||
                                                            hasValue(data.priceSecond)
                                                       );
                                                  })()
                                                  : data.keyString === "productCategory"
                                                       ? (() => {
                                                            const hasDept = Array.isArray(data.department)
                                                                 ? data.department.length > 0
                                                                 : !!data.department;
                                                            const hasArea = Array.isArray(data.area)
                                                                 ? data.area.length > 0
                                                                 : !!data.area;
                                                            const hasCategory = Array.isArray(data.category)
                                                                 ? data.category.length > 0
                                                                 : !!data.category;
                                                            const hasArticle = Array.isArray(data.article)
                                                                 ? data.article.length > 0
                                                                 : !!data.article;
                                                            const hasProvider = Array.isArray(data.provider)
                                                                 ? data.provider.length > 0
                                                                 : !!data.provider;
                                                            const hasProductNrList =
                                                                 typeof data.productNrList === "string" &&
                                                                 data.productNrList.trim().length > 0;
                                                            return (
                                                                 hasDept ||
                                                                 hasArea ||
                                                                 hasCategory ||
                                                                 hasArticle ||
                                                                 hasProvider ||
                                                                 hasProductNrList
                                                            );
                                                       })()
                                                       : true)
                              ),
                              ...productRuleData.stockRules.filter(
                                   (rule) => rule.active && isStockRuleConfigured(rule)
                              ),
                         ]
                    }
               }}
          >
               {children}
          </ProductRuleContext.Provider>
     );
};

/** @returns {ProductRuleContextValue} */
export const useProductRule = () => {
     const context = useContext(ProductRuleContext);
     if (!context) {
          throw new Error("useProductRule muss innerhalb des ProductRuleContextProviders verwendet werden");
     }
     return context;
};